<script setup lang="ts">
// Presentational, scrollable multi-month grid (port of legacy `CalendarView.vue`, 210 ln). Copy is
// caller-supplied (`weekdayLabels`/`monthLabels`); all date math lives in `./logic`. Emits the raw
// clicked `Date` — the root owns the selection state machine.
import { computed, nextTick, onMounted, ref } from 'vue'

import { buildCalendarGrid, type CalendarCell } from './logic'
import type { MonthLabels, WeekdayLabels } from './types'

const props = withDefaults(
  defineProps<{
    weekdayLabels: WeekdayLabels
    monthLabels: MonthLabels
    min?: Date | null
    max: Date
    start?: Date | null
    end?: Date | null
    compareStart?: Date | null
    compareEnd?: Date | null
    compareEnabled?: boolean
  }>(),
  {
    min: null,
    start: null,
    end: null,
    compareStart: null,
    compareEnd: null,
    compareEnabled: false,
  },
)

const emit = defineEmits<{ dayClick: [date: Date] }>()

const scrollContainerRef = ref<HTMLElement | null>(null)

const cells = computed<CalendarCell[]>(() =>
  buildCalendarGrid({
    min: props.min ?? null,
    max: props.max,
    start: props.start ?? null,
    end: props.end ?? null,
    compareStart: props.compareStart ?? null,
    compareEnd: props.compareEnd ?? null,
    compareEnabled: props.compareEnabled,
    monthLabels: props.monthLabels,
  }),
)

function dayButtonClass(cell: CalendarCell): Record<string, boolean> {
  const isPrimaryEdge = Boolean(cell.isStart || cell.isEnd)
  const isCompareEdge = Boolean((cell.isCompareStart || cell.isCompareEnd) && props.compareEnabled)
  const isCompareSelected = Boolean(cell.isCompareSelected && props.compareEnabled)
  return {
    'bg-transparent': !isPrimaryEdge && !isCompareEdge,
    'text-fg': !isPrimaryEdge && !isCompareEdge && !cell.isSelected && !isCompareSelected,
    'is-disabled': Boolean(cell.isDisabled),
    'is-primary-edge font-semibold': isPrimaryEdge,
    'is-compare-edge font-semibold': !isPrimaryEdge && isCompareEdge,
    'is-selected': !isPrimaryEdge && !isCompareEdge && Boolean(cell.isSelected),
    'is-compare-selected':
      !isPrimaryEdge &&
      !isCompareEdge &&
      !cell.isSelected &&
      Boolean(cell.isCompareSelected && props.compareEnabled),
  }
}

function rangeBgClass(cell: CalendarCell): Record<string, boolean> {
  const inPrimary = Boolean(cell.isSelected) && !cell.isDisabled
  const inCompare = Boolean(cell.isCompareSelected && props.compareEnabled) && !cell.isDisabled
  const soloPrimary = Boolean(cell.isStart && cell.isEnd)
  const soloCompare = Boolean(cell.isCompareStart && cell.isCompareEnd)
  return {
    'range-both': inPrimary && inCompare,
    'range-primary': inPrimary && !inCompare && !soloPrimary,
    'range-primary-start rounded-l-full':
      inPrimary && !inCompare && Boolean(cell.isStart) && !soloPrimary,
    'range-primary-end rounded-r-full':
      inPrimary && !inCompare && Boolean(cell.isEnd) && !soloPrimary,
    'range-compare': !inPrimary && inCompare && !soloCompare,
    'range-compare-start rounded-l-full':
      !inPrimary && inCompare && Boolean(cell.isCompareStart) && !soloCompare,
    'range-compare-end rounded-r-full':
      !inPrimary && inCompare && Boolean(cell.isCompareEnd) && !soloCompare,
  }
}

onMounted(() => {
  void nextTick(() => {
    const container = scrollContainerRef.value
    if (!container) return
    const target = props.end ?? props.start ?? props.max
    const targetMonthId = `${target.getFullYear()}-${target.getMonth()}`
    const el = container.querySelector<HTMLElement>(`[data-month-id="${targetMonthId}"]`)
    container.scrollTop = el ? el.offsetTop - 50 : container.scrollHeight
  })
})
</script>

<template>
  <div
    ref="scrollContainerRef"
    class="ui-date-picker__calendar relative h-[450px] w-[17.5rem] overflow-y-auto bg-surface font-sans"
  >
    <div
      class="ui-date-picker__weekdays sticky top-0 z-sticky grid grid-cols-7 bg-surface px-0 pt-2xs pb-xs text-center text-2xs font-semibold text-[var(--ui-color-calendar-weekday)]"
    >
      <div v-for="(label, i) in weekdayLabels" :key="i" class="ui-date-picker__weekday">
        {{ label }}
      </div>
    </div>

    <div class="ui-date-picker__grid grid grid-cols-7 gap-y-0.5 px-2xs">
      <template v-for="(cell, index) in cells" :key="index">
        <div
          v-if="cell.isFullHeader"
          :data-month-id="cell.monthId"
          class="ui-date-picker__month-header ui-date-picker__month-header--full col-span-7 my-xs mt-md pl-2xs text-sm font-semibold text-[var(--ui-color-calendar-month)]"
        >
          {{ cell.text }}
        </div>

        <div v-else class="ui-date-picker__cell flex h-7 items-center justify-start">
          <div
            v-if="cell.isInlineHeader"
            :data-month-id="cell.monthId"
            class="ui-date-picker__month-header ui-date-picker__month-header--inline flex-none pl-2xs text-sm font-semibold text-[var(--ui-color-calendar-month-inline)]"
          >
            {{ cell.text }}
          </div>
          <div
            v-else-if="cell.date"
            class="ui-date-picker__day-bg flex h-full w-full items-center justify-center"
            :class="rangeBgClass(cell)"
          >
            <button
              type="button"
              :disabled="cell.isDisabled"
              class="ui-date-picker__day h-full w-full cursor-pointer rounded-full border-0 text-xs transition-colors duration-150 enabled:hover:bg-[var(--ui-color-calendar-hover)] disabled:cursor-not-allowed disabled:text-[var(--ui-color-calendar-disabled)]"
              :class="dayButtonClass(cell)"
              @click="cell.date && emit('dayClick', cell.date)"
            >
              {{ cell.dayOfMonth }}
            </button>
          </div>
        </div>
        <div
          v-if="cells[index + 1] && cells[index + 1]?.isInlineHeader"
          class="ui-date-picker__row-break col-span-7"
        />
      </template>
    </div>
  </div>
</template>

<style scoped>
.ui-date-picker__calendar .ui-date-picker__day.is-primary-edge {
  background-color: var(--ui-color-calendar-edge);
  color: var(--ui-color-calendar-edge-fg);
}
.ui-date-picker__calendar .ui-date-picker__day.is-compare-edge {
  background-color: var(--ui-color-calendar-compare-edge);
  color: var(--ui-color-calendar-edge-fg);
}
.ui-date-picker__calendar .ui-date-picker__day.is-selected {
  color: var(--ui-color-calendar-range-fg);
}
.ui-date-picker__calendar .ui-date-picker__day.is-compare-selected {
  color: var(--ui-color-calendar-compare-range-fg);
}
.ui-date-picker__calendar .ui-date-picker__day-bg.range-primary,
.ui-date-picker__calendar .ui-date-picker__day-bg.range-primary-start,
.ui-date-picker__calendar .ui-date-picker__day-bg.range-primary-end {
  background-color: var(--ui-color-calendar-range);
}
.ui-date-picker__calendar .ui-date-picker__day-bg.range-compare,
.ui-date-picker__calendar .ui-date-picker__day-bg.range-compare-start,
.ui-date-picker__calendar .ui-date-picker__day-bg.range-compare-end {
  background-color: var(--ui-color-calendar-compare-range);
}
.ui-date-picker__calendar .ui-date-picker__day-bg.range-both {
  background-color: color-mix(in srgb, var(--ui-color-calendar-edge) 12%, transparent);
}
</style>
